import { FC, memo, useCallback, useEffect, useMemo, useState } from 'react';
import { styled } from '@robocorp/theme';
import { Workspace } from '@robocorp/robocloud-internal-cds-sdk';
import { Box, Scroll, SideNavigation, SkeletonLoader } from '@robocorp/components';
import { useLocation } from 'react-router-dom';

import { IconLogoRobocorp } from '@robocorp/icons/logos';
import { useTranslation } from 'react-i18next';
import {
  useWorkspacesQuery,
  usePartnerAccountsQuery,
  useCurrentOrFirstOrganization,
} from '~/queries';
import { CurrentUserRestriction } from '~/components';
import {
  NewWorkspaceButton,
  OtherWorkspacesAccordion,
  OrganizationMenu,
  WorkspaceMenuItem,
} from './menu-items';
import { SideNavigationSkeleton } from '../skeleton';
import { WelcomeLink } from './menu-items/WelcomeLink';

const ContentScroll = styled(Scroll)`
  min-height: 0px;
  margin-top: -2px;

  > div {
    padding: 2px ${({ theme }) => theme.space.$8} 0;
    border-radius: ${({ theme }) => theme.radii.$8};
  }
`;

const Container = styled.div<{ isCollapsed: boolean; isHovered: boolean }>`
  grid-area: aside;
  position: ${({ isCollapsed }) => (isCollapsed ? 'fixed' : 'static')};
  display: ${({ isCollapsed, isHovered }) => (!isCollapsed || isHovered ? 'block' : 'none')};
  height: 100%;
  z-index: 10;
  background: ${({ theme }) => theme.colors.background.primary.color};
  padding-right: ${({ theme }) => theme.space.$16};
  box-shadow: ${({ isCollapsed, theme }) => (isCollapsed ? theme.shadows.small : 'none')};
`;

const Footer: FC = () => {
  const { t } = useTranslation();

  return (
    <Box display="flex" alignItems="center" ml="$24" mt="$24" color="content.subtle.light">
      <IconLogoRobocorp size={24} />
      <Box ml={8} fontSize={12}>
        {t('powered')}
      </Box>
    </Box>
  );
};

interface SideNavigationProps {
  open: boolean;
  onClose: () => void;
  isCollapsed: boolean;
}

interface WorkspacesByAccess {
  workspaces: Workspace[];
  otherWorkspaces: Workspace[];
}

const isVisibleWorkspace = (workspace: Workspace) => workspace.is_agent_workspace !== true;

const SideNavigationConnected: FC<SideNavigationProps> = ({ open, onClose, isCollapsed }) => {
  const [isHovered, setIsHovered] = useState(false);
  const { data: currentOrganization, isLoading: isOrganizationLoading } =
    useCurrentOrFirstOrganization();
  const { data: workspaces = [], isLoading: isWorkspacesLoading } = useWorkspacesQuery();
  usePartnerAccountsQuery();

  const location = useLocation();

  const isOrganizationAdminOrOwner = useMemo(
    () => ['admin', 'owner'].includes(currentOrganization?.roleId || ''),
    [currentOrganization],
  );

  const { workspaces: organizationWorkspaces, otherWorkspaces } = useMemo(() => {
    if (!currentOrganization) return { workspaces: [], otherWorkspaces: [] };

    return workspaces.reduce<WorkspacesByAccess>(
      (acc, ws) => {
        if (ws.organization_id !== currentOrganization.id) return acc;

        if (!ws.permissions?.length && isVisibleWorkspace(ws)) {
          acc.otherWorkspaces.push(ws);
          return acc;
        }

        acc.workspaces.push(ws);
        return {
          ...acc,
          workspaces: acc.workspaces.filter(isVisibleWorkspace),
          otherWorkspaces: acc.otherWorkspaces.filter(isVisibleWorkspace),
        };
      },
      { workspaces: [], otherWorkspaces: [] },
    );
  }, [currentOrganization, workspaces]);

  const [firstWorkspace] = organizationWorkspaces;

  useEffect(() => {
    const onMouseMove = (e: MouseEvent) => {
      if (e.pageX < 40 && !isHovered) {
        setIsHovered(true);
      }
    };

    if (isCollapsed && !isHovered) {
      window.addEventListener('mousemove', onMouseMove, { passive: true });
    }

    return () => {
      window.removeEventListener('mousemove', onMouseMove);
    };
  }, [isCollapsed, isHovered]);

  const onMouseLeave = useCallback(() => {
    setIsHovered(false);
  }, []);

  return (
    <Container isCollapsed={isCollapsed} isHovered={isHovered} onMouseLeave={onMouseLeave}>
      <SideNavigation
        open={open}
        onClose={onClose}
        data-testid="application__sidebar"
        id="application__sidebar"
      >
        <SkeletonLoader
          skeleton={SideNavigationSkeleton}
          loading={isOrganizationLoading || isWorkspacesLoading}
        >
          <>
            <Box mb={14}>
              <OrganizationMenu />
            </Box>

            <WelcomeLink workspace={firstWorkspace} />

            <ContentScroll>
              <Box mb={16}>
                {organizationWorkspaces.map((workspace) => (
                  <WorkspaceMenuItem key={workspace.id} workspace={workspace} />
                ))}
              </Box>

              <CurrentUserRestriction permission="canManageWorkspaces">
                {otherWorkspaces.length > 0 && isOrganizationAdminOrOwner && (
                  <Box mb={16}>
                    <OtherWorkspacesAccordion workspaces={otherWorkspaces} />
                  </Box>
                )}
              </CurrentUserRestriction>
              {!location.pathname.includes('create-workspace') && (
                <NewWorkspaceButton organizationWorkspacesCount={organizationWorkspaces.length} />
              )}
            </ContentScroll>
            <Footer />
          </>
        </SkeletonLoader>
      </SideNavigation>
    </Container>
  );
};

export default memo(SideNavigationConnected);
